/*
  Dynoforce tunekiezer

  Handmatig je motor kiezen op de chiptuningpagina, voor wie geen kenteken bij
  de hand heeft. Drie keuzelijsten: merk, model, uitvoering. De uitkomst gaat
  door dezelfde molen als de kentekenroute, dus de resultaatkaart eronder is
  exact dezelfde kaart.

  Waarom niet de collectie-autokiezer uit het menu: die gaat over onderdelen en
  eindigt bij een modelcollectie. Chiptuning hangt aan de motor, en die staat
  in de tuningdatabase van het platform. Twee verschillende vragen, dus twee
  verschillende lijsten.

  Twee routes op het platform, achter dezelfde app-proxy als de kentekencheck:
  - /apps/kentekencheck/merken                     -> { merken: [ { merk, modellen[] } ] }
  - /apps/kentekencheck/uitvoeringen?merk=&model=  -> { uitvoeringen: [ ... ] }

  Een uitvoering draagt naam, generatie, jaar_vanaf, vermogen en koppel. De
  generatie is niet optioneel om te tonen: dezelfde motornaam komt voor in
  meerdere generaties (de 1.4 TSI 122 pk zit in zowel de Golf V als de VI), dus
  zonder generatie kiest iemand tussen twee gelijke regels. Daarom groeperen we
  de lijst op generatie. Waar de generatie ontbreekt valt het bouwjaar in.
*/
(function () {
  'use strict';

  var PAD = '/apps/kentekencheck';

  /* Ophalen met een tijdslimiet. Zonder limiet blijft een keuzelijst bij een
     trage dienst eindeloos op "Laden" staan zonder dat iemand weet waarom. */
  function haal(url) {
    var af = typeof AbortController === 'function' ? new AbortController() : null;
    var klok = setTimeout(function () { if (af) af.abort(); }, 12000);
    return fetch(url, af ? { signal: af.signal } : undefined).then(function (r) {
      clearTimeout(klok);
      if (!r.ok) throw new Error('Die lijst konden we even niet ophalen.');
      return r.json();
    }).catch(function (e) {
      clearTimeout(klok);
      throw (e && e.name === 'AbortError')
        ? new Error('Het duurt langer dan normaal. Probeer het nog een keer.')
        : new Error('Die lijst konden we even niet ophalen.');
    });
  }

  /* Alleen rijen waar de resultaatkaart mee kan rekenen. Ontbreekt vermogen of
     koppel, dan is die rij geen keuze maar een lege kaart in wording. Zelfde
     eis als df-kenteken.js stelt aan de kentekenroute. */
  function bruikbaar(rijen) {
    if (!Array.isArray(rijen)) return [];
    return rijen.filter(function (u) {
      return u && u.vermogen && u.koppel &&
             typeof u.vermogen.origineel === 'number' && typeof u.vermogen.stage1 === 'number' &&
             typeof u.koppel.origineel === 'number' && typeof u.koppel.stage1 === 'number';
    });
  }

  function groepVan(u) {
    if (u.generatie) return String(u.generatie);
    if (u.jaar_vanaf) return 'vanaf ' + u.jaar_vanaf;
    return '';
  }

  /* Dezelfde motor staat soms twee keer in de lijst, met alleen een ander
     jaar_vanaf: dat is een facelift waar tuningtechnisch niets verandert. Voor
     de bezoeker zijn dat geen twee keuzes maar een. Alleen samenvoegen als
     werkelijk alle cijfers gelijk zijn; verschilt er iets, dan blijven het er
     twee en maakt onderscheid() ze uit elkaar te houden. */
  function ontdubbel(rijen) {
    var gezien = {};
    var uit = [];
    rijen.forEach(function (u) {
      var sleutel = [groepVan(u), u.naam,
        u.vermogen.origineel, u.vermogen.stage1,
        u.koppel.origineel, u.koppel.stage1].join('|');
      if (gezien[sleutel]) return;
      gezien[sleutel] = true;
      uit.push(u);
    });
    return uit;
  }

  /* Blijven er binnen een generatie twee regels met dezelfde naam over, dan
     verschillen ze in de cijfers en niet in de tekst. Zet het koppel erbij,
     anders staat er twee keer hetzelfde in de lijst. Alleen waar het nodig is:
     bij de rest is de naam al genoeg. */
  function onderscheid(rijen) {
    var telling = {};
    rijen.forEach(function (u) {
      var k = groepVan(u) + '|' + u.naam;
      telling[k] = (telling[k] || 0) + 1;
    });
    return rijen.map(function (u) {
      var label = u.naam || 'Onbekende uitvoering';
      if (telling[groepVan(u) + '|' + u.naam] > 1) {
        label += ' · ' + u.koppel.origineel + ' ' + (u.koppel.eenheid || 'Nm');
      }
      return { rij: u, label: label };
    });
  }

  function leeg(sel, tekst) {
    sel.innerHTML = '';
    var o = document.createElement('option');
    o.value = '';
    o.textContent = tekst;
    sel.appendChild(o);
  }

  function init(root) {
    root.querySelectorAll('[data-dft]').forEach(function (blok) {
      if (blok.dataset.dftInit) return;
      blok.dataset.dftInit = '1';

      var selMerk  = blok.querySelector('[data-dft="merk"]');
      var selModel = blok.querySelector('[data-dft="model"]');
      var selUitv  = blok.querySelector('[data-dft="uitvoering"]');
      var stand    = blok.querySelector('[data-dft-stand]');
      if (!selMerk || !selModel || !selUitv) return;

      var sectie = blok.closest('.shopify-section') || document;
      var form = sectie.querySelector('form[data-df-kt]');

      var merken = null;
      var bezigMerken = null;
      var rijenNu = [];
      /* Een voorbeeldfoto van dit model, als het platform er een meestuurt bij
         de uitvoeringen. Leeg zolang die er niet is; de kaart laat het blok dan
         gewoon weg. */
      var fotoNu = null;
      var fotosNu = null;

      function zeg(tekst, fout) {
        if (!stand) return;
        stand.textContent = tekst || '';
        stand.classList.toggle('is-fout', !!fout);
      }

      /* Een lijst van 64 merken is te groot om zomaar bij elk paginabezoek op
         te halen, en te belangrijk om pas te laden als iemand de lijst al
         openklapt. Vandaar bij het eerste teken van belangstelling: focus of
         een aanraking op de merklijst. Daarna staat hij in het geheugen. */
      function laadMerken() {
        if (merken) return Promise.resolve(merken);
        if (bezigMerken) return bezigMerken;
        zeg('Merken laden');
        bezigMerken = haal(PAD + '/merken').then(function (d) {
          merken = (d && Array.isArray(d.merken)) ? d.merken : [];
          vulMerken();
          zeg('');
          return merken;
        }).catch(function (e) {
          bezigMerken = null;
          zeg(e.message, true);
          return [];
        });
        return bezigMerken;
      }

      function vulMerken() {
        leeg(selMerk, selMerk.dataset.leeg || 'Kies je merk');
        merken.forEach(function (m) {
          if (!m || !m.merk) return;
          var o = document.createElement('option');
          o.value = m.merk;
          o.textContent = m.merk;
          selMerk.appendChild(o);
        });
      }

      selMerk.addEventListener('focus', laadMerken);
      selMerk.addEventListener('pointerdown', laadMerken);

      selMerk.addEventListener('change', function () {
        leeg(selModel, selModel.dataset.leeg || 'Kies je model');
        leeg(selUitv, selUitv.dataset.leeg || 'Kies je uitvoering');
        selModel.disabled = true;
        selUitv.disabled = true;
        zeg('');
        if (!selMerk.value || !merken) return;

        var m = merken.filter(function (x) { return x.merk === selMerk.value; })[0];
        var lijst = (m && Array.isArray(m.modellen)) ? m.modellen : [];
        lijst.forEach(function (naam) {
          var o = document.createElement('option');
          o.value = naam;
          o.textContent = naam;
          selModel.appendChild(o);
        });
        selModel.disabled = lijst.length === 0;
        if (!lijst.length) zeg('Voor dit merk staan nog geen modellen in onze database.');
      });

      selModel.addEventListener('change', function () {
        leeg(selUitv, selUitv.dataset.leeg || 'Kies je uitvoering');
        selUitv.disabled = true;
        rijenNu = [];
        fotoNu = null;
        fotosNu = null;
        if (!selMerk.value || !selModel.value) return;

        zeg('Uitvoeringen laden');
        var url = PAD + '/uitvoeringen?merk=' + encodeURIComponent(selMerk.value) +
                  '&model=' + encodeURIComponent(selModel.value);
        haal(url).then(function (d) {
          /* De voorbeeldfoto van het model komt van de kentekendienst en hangt
             aan het kenteken; deze lijst levert er vandaag geen. Geeft het
             platform er later wel een mee - op het antwoord of per uitvoering -
             dan reist hij vanaf hier gewoon mee naar de kaart, zonder dat er
             in het thema nog iets hoeft te veranderen. */
          fotoNu = (d && d.afbeelding) || null;
          fotosNu = (d && d.afbeeldingen) || null;
          var rijen = ontdubbel(bruikbaar(d && d.uitvoeringen));
          if (!rijen.length) {
            zeg('Van dit model staan nog geen tunegegevens in onze database. App ons even, dan zoeken we het voor je uit.');
            return;
          }

          /* De volgorde van de dienst is die van de generaties zelf, van oud
             naar nieuw. Die houden we aan; binnen een generatie sorteren we op
             vermogen, want dan loopt de lijst van klein naar groot. */
          var groepen = [];
          var perGroep = {};
          rijen.forEach(function (u) {
            var g = groepVan(u);
            if (!perGroep[g]) { perGroep[g] = []; groepen.push(g); }
            perGroep[g].push(u);
          });

          rijenNu = [];
          groepen.forEach(function (g) {
            var deel = perGroep[g].slice().sort(function (a, b) {
              return a.vermogen.origineel - b.vermogen.origineel;
            });
            var opties = onderscheid(deel);
            var doel = selUitv;
            if (g) {
              doel = document.createElement('optgroup');
              doel.label = g;
              selUitv.appendChild(doel);
            }
            opties.forEach(function (x) {
              var o = document.createElement('option');
              o.value = String(rijenNu.length);
              o.textContent = x.label;
              doel.appendChild(o);
              rijenNu.push(x.rij);
            });
          });

          selUitv.disabled = false;
          zeg('');
        }).catch(function (e) {
          zeg(e.message, true);
        });
      });

      selUitv.addEventListener('change', function () {
        if (selUitv.value === '') return;
        var u = rijenNu[Number(selUitv.value)];
        if (!u) return;

        /* De resultaatkaart en de keuzelijst zitten in df-kenteken.js. Die
           laat per formulier een haakje achter, zodat een handmatige keuze
           precies dezelfde kaart vult als een kenteken. Zonder dat haakje
           doet deze kiezer niets zichtbaars, en dan is een melding beter dan
           een dropdown die in het niets grijpt. */
        var api = form && form.dfKt;
        if (!api || typeof api.kies !== 'function') {
          zeg('De resultaatkaart is nog niet geladen. Ververs de pagina even.', true);
          return;
        }
        zeg('');
        var antwoord = {
          merk: selMerk.value,
          model: selModel.value,
          uitvoeringen: rijenNu,
          gevonden: true,
          status: 'zeker'
        };
        var foto = u.afbeelding || fotoNu;
        var fotos = u.afbeeldingen || fotosNu;
        if (foto) antwoord.afbeelding = foto;
        if (fotos) antwoord.afbeeldingen = fotos;
        api.kies(antwoord, u);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
